import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Avatar,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  KeyboardArrowDown,
  AdminPanelSettings,
  Notifications as NotificationsIcon,
  Search as SearchIcon,
  Home as HomeIcon,
  ExpandMore as ExpandMoreIcon,
  Analytics as AnalyticsIcon,
  AssignmentTurnedIn as ReservationIcon,
  Gavel as GavelIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Inventory as ListingsIcon,
  FavoriteBorder as FavoritesIcon,
  Description as RequestsIcon,
  Add as AddIcon,
  Logout as LogoutIcon,
  Receipt as ReceiptIcon,
  AccountBalanceWallet as WalletIcon,
  ShoppingCart as ProductsIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import useCompleteUser from '../../hooks/useCompleteUser';
import NotificationCenter from '../notifications/NotificationCenter';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../common/i18n/LanguageSwitcher';
import BalancesPanel from '../common/BalancesPanel';
import logoKrillou from '../common/ressources/logo-krillou.png';

const Header = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { completeUser, loading: userLoading, error: userError } = useCompleteUser();
  const navigate = useNavigate();
  const location = useLocation(); // Pour détecter les changements de route
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [infoMenuAnchor, setInfoMenuAnchor] = useState(null);
  const [listingsSubMenuAnchor, setListingsSubMenuAnchor] = useState(null);
  const { t } = useTranslation(['common', 'disputes', 'penalties']);
  
  // Auto-fermeture des menus lors des changements de route
  useEffect(() => {
    setAnchorEl(null);
    setMobileOpen(false);
    setListingsSubMenuAnchor(null);
  }, [location.pathname]);
  
  // Fonction pour tronquer le nom d'utilisateur
  const truncateUserName = (name, maxLength = 15) => {
    if (!name) return '';
    return name.length > maxLength ? `${name.substring(0, maxLength)}...` : name;
  };
  // Fonction de vérification d'accès admin modifiée pour utiliser completeUser avec fallback
  const hasAdminAccess = () => {
    // Si l'utilisateur de base a le flag isAdmin, donner accès immédiatement
    if (user?.isAdmin) return true;

    // Si completeUser n'est pas disponible mais qu'on a l'utilisateur de base
    if (!completeUser && user) {
      // Vérifier les rôles de base via les flags booléens
      if (user.isAdmin) return true;

      // Si on a des userRoles dans l'objet user (cas possible selon l'implémentation)
      if (user.userRoles && Array.isArray(user.userRoles) && user.userRoles.length > 0) {
        // Définir les rôles de base
        const baseRoles = ['tenant', 'owner', 'user'];

        // Vérifier si l'utilisateur a au moins un rôle qui n'est pas un rôle de base
        const hasNonBaseRole = user.userRoles.some(roleObj =>
            !baseRoles.includes(roleObj.role)
        );

        if (hasNonBaseRole) return true;
        if (user.userRoles.length > 1) return true;
      }

      return false;
    }

    // Si completeUser est disponible
    if (completeUser) {
      // Vérifier si completeUser a le flag isAdmin
      if (completeUser.isAdmin) return true;

      // Vérifier si l'utilisateur a des rôles
      if (completeUser.roles && Array.isArray(completeUser.roles) && completeUser.roles.length > 0) {
        // Définir les rôles de base qui ne donnent pas automatiquement accès à l'admin
        const baseRoles = ['tenant', 'owner', 'user'];

        // Vérifier si l'utilisateur a au moins un rôle qui n'est pas un rôle de base
        const hasNonBaseRole = completeUser.roles.some(role =>
            !baseRoles.includes(role)
        );

        // Si l'utilisateur a au moins un rôle qui n'est pas un rôle de base, donner accès
        if (hasNonBaseRole) {
          return true;
        }

        // Si l'utilisateur a plus d'un rôle (même si ce sont des rôles de base), donner accès
        if (completeUser.roles.length > 1) {
          return true;
        }
      }
    }

    // Par défaut, ne pas donner accès
    return false;
  };

  // Pour le débogage - afficher les rôles dans la console
  React.useEffect(() => {
    if (completeUser) {
      console.log('CompleteUser roles:', completeUser.roles);
    }
    if (user && user.userRoles) {
      console.log('User roles from basic user:', user.userRoles);
    }
  }, [completeUser, user]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setListingsSubMenuAnchor(null);
  };

  // Fonction spéciale pour navigation vers Centre de Réservations
  // MODIFICATION UX: Navigation directe vers vue owner
  const handleReservationsHubNavigation = (event) => {
    event.preventDefault(); // Empêche la navigation par défaut du RouterLink
    setAnchorEl(null);      // Ferme le menu immédiatement
    // Double sécurité avec setTimeout pour forcer la fermeture
    setTimeout(() => {
      setAnchorEl(null);
      // Navigation directe vers la vue réservations propriétaire
      navigate('/reservations-owner');
    }, 0);
  };

  // Fonction similaire pour navigation vers Centre Financier desktop
  const handleFinancialCenterNavigation = (event) => {
    event.preventDefault(); // Empêche la navigation par défaut du RouterLink
    setAnchorEl(null);      // Ferme le menu immédiatement
    // Double sécurité avec setTimeout pour forcer la fermeture
    setTimeout(() => {
      setAnchorEl(null);
      // Navigation vers la vue financière propriétaire
      navigate('/financial-owner');
    }, 0);
  };

  // Fonction spéciale pour navigation mobile vers Centre de Réservations
  // MODIFICATION UX: Navigation directe vers vue owner
  const handleReservationsHubNavigationMobile = () => {
    setMobileOpen(false);   // Ferme le drawer mobile immédiatement
    // Double sécurité avec setTimeout
    setTimeout(() => {
      setMobileOpen(false);
      // Navigation directe vers la vue réservations propriétaire
      navigate('/reservations-owner');
    }, 0);
  };

  // Fonction similaire pour le centre financier mobile
  const handleFinancialCenterNavigationMobile = () => {
    setMobileOpen(false);   // Ferme le drawer mobile immédiatement
    // Double sécurité avec setTimeout
    setTimeout(() => {
      setMobileOpen(false);
      // Navigation vers la vue financière propriétaire
      navigate('/financial-owner');
    }, 0);
  };

  const handleInfoMenuOpen = (event) => {
    setInfoMenuAnchor(event.currentTarget);
  };

  const handleInfoMenuClose = () => {
    setInfoMenuAnchor(null);
  };

  const handleListingsSubMenuOpen = (event) => {
    setListingsSubMenuAnchor(event.currentTarget);
  };

  const handleListingsSubMenuClose = () => {
    setListingsSubMenuAnchor(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate('/');
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header utilisateur pour authenticated users */}
      {isAuthenticated ? (
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', backgroundColor: '#f5f5f5' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {(completeUser?.profilePicture || user?.profilePicture) ? (
              <Avatar src={completeUser?.profilePicture || user?.profilePicture} sx={{ width: 48, height: 48, mr: 2 }} />
            ) : (
              <Avatar sx={{ width: 48, height: 48, mr: 2, backgroundColor: '#1976d2' }}>
                {(completeUser?.name || user?.name)?.[0]?.toUpperCase() || 'U'}
              </Avatar>
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                {truncateUserName(completeUser?.name || user?.name, 12)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                {completeUser?.email || user?.email}
              </Typography>
            </Box>
          </Box>
          <Button 
            fullWidth 
            variant="outlined" 
            size="small" 
            startIcon={<SettingsIcon />}
            component={RouterLink}
            to="/profile"
            onClick={() => setMobileOpen(false)}
          >
            {t('navigation.profile')}
          </Button>
        </Box>
      ) : null}

      {/* Navigation scrollable */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List>
          {/* SECTION: Navigation Principale */}
          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/" onClick={() => setMobileOpen(false)}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                <Box sx={{ color: '#1976d2' }}>
                  <HomeIcon />
                </Box>
                <ListItemText primary={t('navigation.home')} />
              </Box>
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/search" onClick={() => setMobileOpen(false)}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                <Box sx={{ color: '#1976d2' }}>
                  <SearchIcon />
                </Box>
                <ListItemText primary={t('navigation.search', 'Recherche')} />
              </Box>
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/categories" onClick={() => setMobileOpen(false)}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                <Box sx={{ color: '#ff9800' }}>
                  <ListingsIcon />
                </Box>
                <ListItemText primary={t('navigation.categories')} />
              </Box>
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/how-it-works" onClick={() => setMobileOpen(false)}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                <Box sx={{ color: '#4caf50' }}>
                  <DashboardIcon />
                </Box>
                <ListItemText primary={t('navigation.howItWorks')} />
              </Box>
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/about" onClick={() => setMobileOpen(false)}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                <Box sx={{ color: '#9c27b0' }}>
                  <PersonIcon />
                </Box>
                <ListItemText primary={t('navigation.about')} />
              </Box>
            </ListItemButton>
          </ListItem>

          {isAuthenticated ? (
            <>
              <Divider sx={{ my: 1 }} />

              {/* SECTION: Mon Compte */}
              <Typography variant="caption" sx={{ px: 2, py: 1, color: '#999', fontWeight: 600, display: 'block' }}>
                {t('navigation.myAccount')}
              </Typography>

              <ListItem disablePadding>
                <ListItemButton component={RouterLink} to="/dashboard" onClick={() => setMobileOpen(false)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                    <Box sx={{ color: '#1976d2' }}>
                      <DashboardIcon />
                    </Box>
                    <ListItemText primary={t('navigation.dashboard')} />
                  </Box>
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton component={RouterLink} to="/notifications" onClick={() => setMobileOpen(false)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                    <Box sx={{ color: '#ff9800' }}>
                      <NotificationsIcon />
                    </Box>
                    <ListItemText primary={t('navigation.notifications')} />
                  </Box>
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton component={RouterLink} to="/notifications/preferences" onClick={() => setMobileOpen(false)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                    <Box sx={{ color: '#757575' }}>
                      <SettingsIcon />
                    </Box>
                    <ListItemText primary={t('navigation.notificationPreferences')} />
                  </Box>
                </ListItemButton>
              </ListItem>

              <Divider sx={{ my: 1 }} />

              {/* SECTION: Favoris & Demandes */}
              <Typography variant="caption" sx={{ px: 2, py: 1, color: '#999', fontWeight: 600, display: 'block' }}>
                {t('common.myContent', 'Mon Contenu')}
              </Typography>

              <ListItem disablePadding>
                <ListItemButton component={RouterLink} to="/favorites" onClick={() => setMobileOpen(false)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                    <Box sx={{ color: '#e91e63' }}>
                      <FavoritesIcon />
                    </Box>
                    <ListItemText primary={t('common.favorites', 'Favoris')} />
                  </Box>
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton component={RouterLink} to="/my-requests" onClick={() => setMobileOpen(false)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                    <Box sx={{ color: '#5d4037' }}>
                      <RequestsIcon />
                    </Box>
                    <ListItemText primary={t('navigation.myRequests', 'Mes demandes')} />
                  </Box>
                </ListItemButton>
              </ListItem>

              <Divider sx={{ my: 1 }} />

              {/* SECTION: Gestion des Annonces */}
              <Typography variant="caption" sx={{ px: 2, py: 1, color: '#999', fontWeight: 600, display: 'block' }}>
                {t('navigation.listingsManagement')}
              </Typography>

              <ListItem disablePadding>
                <ListItemButton component={RouterLink} to="/my-listings" onClick={() => setMobileOpen(false)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                    <Box sx={{ color: '#2e7d32' }}>
                      <ListingsIcon />
                    </Box>
                    <Box>
                      <ListItemText primary={t('navigation.myListings')} />
                      <Typography variant="caption" sx={{ color: '#999' }}>
                        {t('navigation.myListingsDescription')}
                      </Typography>
                    </Box>
                  </Box>
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton component={RouterLink} to="/my-products" onClick={() => setMobileOpen(false)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                    <Box sx={{ color: '#ff6f00' }}>
                      <ProductsIcon />
                    </Box>
                    <Box>
                      <ListItemText primary={t('navigation.myProducts')} />
                      <Typography variant="caption" sx={{ color: '#999' }}>
                        {t('navigation.myProductsDescription')}
                      </Typography>
                    </Box>
                  </Box>
                </ListItemButton>
              </ListItem>

              <Divider sx={{ my: 1 }} />

              {/* SECTION: Transactions & Finances */}
              <Typography variant="caption" sx={{ px: 2, py: 1, color: '#999', fontWeight: 600, display: 'block' }}>
                {t('navigation.transactions', 'Transactions')}
              </Typography>              

              <ListItem disablePadding>
                <ListItemButton onClick={() => { handleReservationsHubNavigationMobile(); }} >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                    <Box sx={{ color: '#d32f2f' }}>
                      <ReservationIcon />
                    </Box>
                    <ListItemText primary={t('navigation.reservationCenter')} />
                  </Box>
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton onClick={() => { handleFinancialCenterNavigationMobile(); }} >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                    <Box sx={{ color: '#4caf50' }}>
                      <DashboardIcon />
                    </Box>
                    <ListItemText primary={t('navigation.financesCenter')} />
                  </Box>
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton component={RouterLink} to="/owner/invoices" onClick={() => setMobileOpen(false)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                    <Box sx={{ color: '#7b1fa2' }}>
                      <ReceiptIcon />
                    </Box>
                    <ListItemText primary={t('navigation.myInvoices', 'Mes Factures')} />
                  </Box>
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton component={RouterLink} to="/owner/payouts" onClick={() => setMobileOpen(false)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                    <Box sx={{ color: '#6200ee' }}>
                      <WalletIcon />
                    </Box>
                    <ListItemText primary={t('navigation.myPayouts', 'Mes Virements')} />
                  </Box>
                </ListItemButton>
              </ListItem>

              <Divider sx={{ my: 1 }} />

              {/* SECTION: Litige & Pénalités */}
              <Typography variant="caption" sx={{ px: 2, py: 1, color: '#999', fontWeight: 600, display: 'block' }}>
                {t('navigation.disputes', 'Contentieux')}
              </Typography>

              <ListItem disablePadding>
                <ListItemButton component={RouterLink} to="/disputes" onClick={() => setMobileOpen(false)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                    <Box sx={{ color: '#1976d2' }}>
                      <GavelIcon />
                    </Box>
                    <ListItemText primary={t('disputes:all.title', 'Mes litiges')} />
                  </Box>
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton component={RouterLink} to="/penalties" onClick={() => setMobileOpen(false)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                    <Box sx={{ color: '#c62828' }}>
                      <GavelIcon />
                    </Box>
                    <ListItemText primary={t('penalties:all.title', 'Mes pénalités')} />
                  </Box>
                </ListItemButton>
              </ListItem>

              {/* Administration section if user has access */}
              {hasAdminAccess() && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" sx={{ px: 2, py: 1, color: '#999', fontWeight: 600, display: 'block' }}>
                    {t('navigation.administration')}
                  </Typography>
                  <ListItem disablePadding>
                    <ListItemButton
                      component={RouterLink}
                      to="/admin"
                      onClick={() => setMobileOpen(false)}
                      sx={{ borderLeft: '3px solid #e91e63', pl: 1.8 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                        <Box sx={{ color: '#e91e63' }}>
                          <AdminPanelSettings />
                        </Box>
                        <ListItemText primary={t('navigation.admin')} sx={{ fontWeight: 600 }} />
                      </Box>
                    </ListItemButton>
                  </ListItem>
                </>
              )}
            </>
          ) : (
            <>
              <Divider sx={{ my: 1 }} />
              <ListItem disablePadding>
                <ListItemButton component={RouterLink} to="/login" onClick={() => setMobileOpen(false)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                    <Box sx={{ color: '#1976d2' }}>
                      <PersonIcon />
                    </Box>
                    <ListItemText primary={t('navigation.login')} />
                  </Box>
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component={RouterLink} to="/register" onClick={() => setMobileOpen(false)}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                    <Box sx={{ color: '#4caf50' }}>
                      <AddIcon />
                    </Box>
                    <ListItemText primary={t('navigation.register')} />
                  </Box>
                </ListItemButton>
              </ListItem>
            </>
          )}
        </List>
      </Box>

      {/* Footer avec langue et devise */}
      <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', backgroundColor: '#fafafa' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Bouton de soldes en mode drawer */}
          <BalancesPanel variant="drawer" />
          
          {/* Langue et Devise en ligne */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <LanguageSwitcher variant="icon" />
            
          </Box>
        </Box>
        {isAuthenticated && (
          <Button 
            fullWidth 
            variant="outlined" 
            color="error"
            size="small"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ mt: 1.5 }}
          >
            {t('navigation.logout')}
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
      <AppBar position="static" color="default" elevation={1}>
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            {/* Logo et titre */}
            <Box
              component={RouterLink}
              to="/"
              sx={{
                mr: 2,
                display: 'inline-flex',
                alignItems: 'center',
                flexGrow: { xs: 1, md: 0 },
                textDecoration: 'none',
                lineHeight: 0
              }}
            >
              <Box
                component="img"
                src={logoKrillou}
                alt="Krillou"
                sx={{
                  // Use larger explicit heights instead of CSS scaling to avoid pixelation
                  height: { xs: 60, sm: 72, md: 78 },
                  transformOrigin: 'left center',
                  imageRendering: 'auto',
                  width: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                  mixBlendMode: 'multiply'
                }}
              />
            </Box>

            {/* Cloche de notifications mobile */}
            {isAuthenticated && (
              <Box sx={{ display: { xs: 'flex', lg: 'none' }, mr: 0.5 }}>
                <NotificationCenter />
              </Box>
            )}

            {/* Bouton menu mobile */}
            <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2, display: { lg: 'none' } }}
            >
              <MenuIcon />
            </IconButton>

            {/* Navigation desktop */}
 
                <Box sx={{ flexGrow: 1, display: { xs: 'none', lg: 'flex' }, ml: 3 }}>
                  <Button color="inherit" component={RouterLink} to="/" sx={{ mx: 0.5 }}>
                    <Typography variant="body1">{t('navigation.home')}</Typography>
                  </Button>
                  <Button 
                    color="inherit" 
                    component={RouterLink} 
                    to="/search" 
                    sx={{ 
                      mx: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      '&:hover': {
                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                      }
                    }}
                  >
                    <SearchIcon fontSize="small" />
                    <Typography variant="body1">{t('navigation.search', 'Recherche')}</Typography>
                  </Button>
                  <Button color="inherit" component={RouterLink} to="/categories" sx={{ mx: 0.5 }}>
                    {t('navigation.categories')}
                  </Button>                 
                  
                  {/* Menu déroulant "Informations" */}
                  <Button 
                    color="inherit" 
                    onClick={handleInfoMenuOpen}
                    endIcon={<ExpandMoreIcon />}
                    sx={{ mx: 0.5 }}
                  >
                    {t('navigation.information', 'Informations')}
                  </Button>
                  <Menu
                    anchorEl={infoMenuAnchor}
                    open={Boolean(infoMenuAnchor)}
                    onClose={handleInfoMenuClose}
                    keepMounted
                  >
                    <MenuItem component={RouterLink} to="/how-it-works" onClick={handleInfoMenuClose}>
                      {t('navigation.howItWorks')}
                    </MenuItem>
                    <MenuItem component={RouterLink} to="/about" onClick={handleInfoMenuClose}>
                      {t('navigation.about')}
                    </MenuItem>
                  </Menu>
                </Box>

            {/* Boutons d'authentification desktop */}
            <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center' }}>
              {isAuthenticated ? (
                  <>
                    {/* Centre de notifications */}
                    <NotificationCenter />

                    {/* Bouton administration pour desktop s'affichant si hasAdminAccess() retourne true */}
                    {hasAdminAccess() && (
                    <Button
                      color="primary"
                      variant="contained"
                      component={RouterLink}
                      to="/admin"
                      startIcon={<AdminPanelSettings />}
                      sx={{
                        mr: 2,
                        background: theme => `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
                        boxShadow: '0 3px 5px 2px rgba(33, 150, 243, .3)',
                      }}
                    >
                      {t('navigation.admin')}
                    </Button>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    
                    <Tooltip 
                      title={t('common.greeting', { name: completeUser?.name || user?.name })}
                      arrow
                      placement="bottom"
                    >
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          mr: 1,
                          maxWidth: 150, // Limite la largeur
                          whiteSpace: 'nowrap', // Empêche les retours à la ligne
                          overflow: 'hidden', // Cache le débordement
                          textOverflow: 'ellipsis', // Ajoute les points de suspension
                          cursor: 'default' // Indique que c'est interactif
                        }}
                      >
                        {t('common.greeting', { name: truncateUserName(completeUser?.name || user?.name) })}
                      </Typography>
                    </Tooltip>
                    
                    <Button
                      color="inherit"
                      onClick={handleMenuOpen}
                      endIcon={<KeyboardArrowDown />}
                      startIcon={
                        (completeUser?.profilePicture || user?.profilePicture) ? (
                          <Avatar src={completeUser?.profilePicture || user?.profilePicture} sx={{ width: 24, height: 24 }} />
                        ) : (
                          <AccountCircle />
                        )
                      }
                    >
                      {t('navigation.myAccount')}
                    </Button>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                      keepMounted
                      PaperProps={{ sx: { maxHeight: 'none' } }}
                    >
                      <MenuItem component={RouterLink} to="/dashboard" onClick={handleMenuClose}>
                        <DashboardIcon fontSize="small" sx={{ mr: 1, color: '#1976d2' }} />
                        {t('navigation.dashboard')}
                      </MenuItem>
                      <MenuItem component={RouterLink} to="/profile" onClick={handleMenuClose}>
                        <PersonIcon fontSize="small" sx={{ mr: 1, color: '#9c27b0' }} />
                        {t('navigation.profile')}
                      </MenuItem>
                      <MenuItem component={RouterLink} to="/notifications" onClick={handleMenuClose}>
                        <NotificationsIcon fontSize="small" sx={{ mr: 1, color: '#ff9800' }} />
                        {t('navigation.notifications')}
                      </MenuItem>                     
                      <MenuItem component={RouterLink} to="/favorites" onClick={handleMenuClose}>
                        <FavoritesIcon fontSize="small" sx={{ mr: 1, color: '#e91e63' }} />
                        {t('common.favorites', 'Favoris')}
                      </MenuItem>                       
                      <MenuItem onClick={handleReservationsHubNavigation}>
                        <ReservationIcon fontSize="small" sx={{ mr: 1, color: '#d32f2f' }} />
                        {t('navigation.reservationCenter')}
                      </MenuItem>
                      <MenuItem onClick={handleFinancialCenterNavigation}>
                        <AccountCircle fontSize="small" sx={{ mr: 1, color: '#4caf50' }} />
                        {t('navigation.financesCenter')}
                      </MenuItem>
                      <MenuItem component={RouterLink} to="/owner/invoices" onClick={handleMenuClose}>
                        <ReceiptIcon fontSize="small" sx={{ mr: 1, color: '#7b1fa2' }} />
                        {t('navigation.myInvoices', 'Mes Factures')}
                      </MenuItem>
                      <MenuItem component={RouterLink} to="/owner/payouts" onClick={handleMenuClose}>
                        <WalletIcon fontSize="small" sx={{ mr: 1, color: '#6200ee' }} />
                        {t('navigation.myPayouts', 'Mes Virements')}
                      </MenuItem>
                      <MenuItem
                          onMouseEnter={handleListingsSubMenuOpen}
                          onClick={handleListingsSubMenuOpen}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ListingsIcon fontSize="small" sx={{ mr: 1, color: '#2e7d32' }} />
                            {t('navigation.listingsManagement')}
                          </Box>

                          <ExpandMoreIcon
                            fontSize="small"
                            sx={{
                              ml: 'auto',
                              transform: Boolean(listingsSubMenuAnchor)
                                ? 'rotate(180deg)'
                                : 'rotate(0deg)',
                              transition: 'transform 0.25s ease'
                            }}
                          />
                        </MenuItem>

                        <Menu
                          anchorEl={listingsSubMenuAnchor}
                          open={Boolean(listingsSubMenuAnchor)}
                          onClose={handleListingsSubMenuClose}
                          anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right'
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left'
                          }}
                          onMouseLeave={handleListingsSubMenuClose}
                        >
                          <MenuItem
                            component={RouterLink}
                            to="/my-listings"
                            onClick={handleMenuClose}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              minWidth: 220
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                              <ListingsIcon
                                fontSize="small"
                                sx={{ mr: 1, color: '#2e7d32' }}
                              />
                              <Typography variant="body2">
                                {t('navigation.myListings')}
                              </Typography>
                            </Box>

                            <Typography
                              variant="caption"
                              sx={{ color: '#999', ml: 4, mt: 0.5 }}
                            >
                              {t('navigation.myListingsDescription')}
                            </Typography>
                          </MenuItem>

                          <MenuItem
                            component={RouterLink}
                            to="/my-products"
                            onClick={handleMenuClose}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              minWidth: 220
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                              <ProductsIcon
                                fontSize="small"
                                sx={{ mr: 1, color: '#ff6f00' }}
                              />
                              <Typography variant="body2">
                                {t('navigation.myProducts')}
                              </Typography>
                            </Box>

                            <Typography
                              variant="caption"
                              sx={{ color: '#999', ml: 4, mt: 0.5 }}
                            >
                              {t('navigation.myProductsDescription')}
                            </Typography>
                          </MenuItem>
                        </Menu>
                      <MenuItem component={RouterLink} to="/disputes" onClick={handleMenuClose}>
                        <GavelIcon fontSize="small" sx={{ mr: 1, color: '#1976d2' }} />
                        {t('disputes:all.title', 'Mes litiges')}
                      </MenuItem>
                      <MenuItem component={RouterLink} to="/penalties" onClick={handleMenuClose}>
                        <GavelIcon fontSize="small" sx={{ mr: 1, color: '#1976d2' }} />
                        {t('penalties:all.title', 'Mes pénalités')}
                      </MenuItem>
                      <MenuItem component={RouterLink} to="/my-requests" onClick={handleMenuClose}>
                        <RequestsIcon fontSize="small" sx={{ mr: 1, color: '#5d4037' }} />
                        {t('navigation.myRequests', 'Mes demandes')}
                      </MenuItem>                     
                       <MenuItem component={RouterLink} to="/notifications/preferences" onClick={handleMenuClose}>
                        <SettingsIcon fontSize="small" sx={{ mr: 1, color: '#757575' }} />
                        {t('navigation.notificationPreferences')}
                      </MenuItem>
                      {/* Option d'administration dans le menu déroulant pour l'accès admin */}
                      {hasAdminAccess() && (
                        <MenuItem
                          component={RouterLink}
                          to="/admin"
                          onClick={handleMenuClose}
                          sx={{ color: 'primary.main' }}
                        >
                          <AdminPanelSettings fontSize="small" sx={{ mr: 1, color: '#e91e63' }} />
                          {t('navigation.admin')}
                        </MenuItem>
                      )}
                      <Divider />
                      <MenuItem onClick={handleLogout}>
                        <LogoutIcon fontSize="small" sx={{ mr: 1, color: '#f44336' }} />
                        {t('navigation.logout')}
                      </MenuItem>
                    </Menu>
                  </Box>
                </>
              ) : (
                <>
                  <Button color="inherit" component={RouterLink} to="/login" sx={{ mr: 1 }}>
                    {t('navigation.login')}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    component={RouterLink}
                    to="/register"
                  >
                    {t('navigation.register')}
                  </Button>                  
                </>                
              )}
              <LanguageSwitcher variant="icon" />
              <BalancesPanel />
            </Box>
          </Toolbar>
        </Container>

        {/* Drawer pour mobile */}
        <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', lg: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
            }}
        >
          {drawer}
        </Drawer>
      </AppBar>
  );
};

export default Header;